import {
  faCaretDown, faCaretUp, faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, type PropsWithChildren, type ReactNode, useEffect, useState,
} from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { HeightTransitionComponent } from '../../components/heightTransition/heightTransition.component';
import {
  alertContainerStyle, buttonStyle,
} from './alert.styles';

export type AlertType = 'danger' | 'info' | 'warning' | 'success' | 'dangerAlternative' | 'successAlternative';

export type AlertSize = 'small' | 'default';

type StylingOptions = Readonly<{
  reverseColors?: boolean;
  transparentBackground?: boolean;
  withoutBorder?: boolean;
}>;

export type AlertProps = Readonly<PropsWithChildren<{
  autoCloseTimeout?: number;
  className?: string;
  collapsedContent?: ReactNode;
  size?: AlertSize;
  stylingOptions?: StylingOptions;
  type: AlertType;

  onClose?: () => void;
}>>;

export const AlertComponent: FC<AlertProps> = ({
  autoCloseTimeout,
  className,
  collapsedContent,
  size = 'default',
  stylingOptions: { reverseColors, withoutBorder, transparentBackground } = {},
  type,
  onClose,
  ...restProps
}) => {
  const collapseEnabled = !!collapsedContent;

  const [collapsed, setCollapsed] = useState(collapseEnabled);

  useEffect(() => {
    if (!autoCloseTimeout || !onClose) {
      return;
    }

    const timeoutId = setTimeout(() => {
      onClose();
    }, autoCloseTimeout);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [onClose, autoCloseTimeout]);

  return (
    <div
      className={className}
      css={alertContainerStyle({ type, size, reverseColors, transparentBackground, withoutBorder, hasCloseButton: !!onClose })}
    >
      {collapseEnabled && (
        <HeightTransitionComponent animateOnMount={false}>
          {collapsed ? collapsedContent : restProps.children}
        </HeightTransitionComponent>
      )}

      {!collapseEnabled && restProps.children}

      {collapsedContent && (
        <button
          css={buttonStyle}
          onClick={() => setCollapsed((previous) => !previous)}
        >
          <FontAwesomeIcon icon={collapsed ? faCaretDown : faCaretUp} />
        </button>
      )}

      {onClose && (
        <button
          css={buttonStyle}
          onClick={onClose}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      )}
    </div>
  );
};
